<script setup lang="ts">
import { useRouter, RouterLink } from 'vue-router'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import FormTextArea from '@/components/inputs/FormTextArea.vue'
import { getRollsList, sendRollsToSupplier } from '@/api/rolls'
import { getBatchList } from '@/api/batches'
import { onMounted, ref, computed } from 'vue'
import { getProductList } from '@/api/products'
import { getSupplierList } from '@/api/suppliers'
import Checkbox from 'primevue/checkbox'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { sendRollsToSupplierSchema } from '@/schemas/batchSchema'
import { useToast } from 'primevue/usetoast'
import { PricingType } from '@/constants/pricing-type'
import ProgressSpinner from 'primevue/progressspinner'
import type { Product } from '@/types/product-types'
import type { Supplier } from '@/types/supplier-types'
import type { Batch, Roll } from '@/types/batch-types'

const { values, handleSubmit, errors, setErrors } = useForm({
  validationSchema: toTypedSchema(sendRollsToSupplierSchema)
})

const toast = useToast()
const loading = ref(false)
const isLoadingData = ref(false)

const showToast = (type: 'success' | 'error', title: string, message: string) => {
  toast.add({
    severity: type,
    summary: title,
    detail: message,
    life: 5000
  })
}

const productsList = ref<Product[]>([])
const supplierList = ref<Supplier[]>([])
const router = useRouter()
const rollList = ref<Roll[]>([])
const selectedRolls = ref<any>([])
const batchList = ref<Batch[]>([])

const batchMap = computed(() => {
  return new Map(batchList.value.map(batch => [batch.id, batch]))
})

const filteredRollList = computed(() => {
  return rollList.value.filter((roll: Roll) =>
    roll.rollState === 0 &&
    (!values.knittingQualityId || roll.knittingQualityId === values.knittingQualityId)
  )
})

const filteredSuppliers = computed(() => {
  return supplierList.value?.filter((supplier: any) =>
    supplier.supplierPricings.some((pricing: any) => pricing.pricingType === PricingType.Finishing)
  ) || []
})

const supplierAddress = computed(() => {
  const supplier = supplierList.value.find((supplier: any) => supplier.id === values.finishingSupplierId)
  return supplier && supplier?.addresses ? supplier.addresses : []
})

const formattedSupplierAddress = computed(() => {
  return supplierAddress.value.map((address: any) => ({
    ...address,
    label: [address.line1, address.line2, address.city, address.region, address.postCode, address.country]
      .filter(Boolean)
      .join(', ')
  }))
})

const selectAll = computed({
  get: () => selectedRolls.value.length === filteredRollList.value.length && filteredRollList.value.length > 0,
  set: (value: boolean) => {
    if (value) {
      selectedRolls.value = filteredRollList.value.map((roll: any) => roll.id)
    } else {
      selectedRolls.value = []
    }
  }
})

const handleRollSelection = (rollId: string) => {
  const index = selectedRolls.value.indexOf(rollId)
  if (index > -1) {
    selectedRolls.value.splice(index, 1)
  } else {
    selectedRolls.value.push(rollId)
  }
}

const isRollSelected = (rollId: string) => {
  return selectedRolls.value.includes(rollId)
}

const submit = handleSubmit(async () => {
  if (selectedRolls.value.length === 0) {
    showToast('error', 'Error', 'Please select at least one roll to send.')
    return
  }

  if (!values.batchNumber) {
    showToast('error', 'Error', 'Please enter a batch number.')
    return
  }

  const formData = {
    batchNumber: values.batchNumber,
    finishingSupplierId: values.finishingSupplierId,
    finishingSupplierName: supplierList.value.find((s: any) => s.id === values.finishingSupplierId)?.name || '',
    finishingSupplierAddressId: values.finishingSupplierAddressId,
    purchaseOrderNumber: values.purchaseOrderNumber,
    knittingQualityId: values.knittingQualityId,
    finishingQualityId: values.finishingQualityId,
    bay: values.bay,
    notes: values.notes,
    rolls: selectedRolls.value.map((rollId: string) => ({ rollId }))
  }

  try {
    loading.value = true
    await sendRollsToSupplier(formData)
    showToast('success', 'Rolls Sent to Supplier!', 'You have successfully created a new batch and sent rolls to supplier.')
    await router.push('/rolls')
  } catch (error: any) {
    console.error('Error sending rolls:', error)
    const errorMessage = error.response?.data?.errors?.[0]?.description || 'Failed to send rolls to supplier.'
    showToast('error', 'Error', errorMessage)
  } finally {
    loading.value = false
  }
})

const getBatchNumber = (batchId: string | null): string => {
  if (!batchId) {
    return 'N/A'
  }
  const foundBatch = batchMap.value.get(batchId)
  return foundBatch ? (foundBatch.batchNumber ?? 'Unknown Batch') : 'Unknown Batch'
}

onMounted(async () => {
  isLoadingData.value = true
  try {
    const [rolls, products, suppliers, batches] = await Promise.all([
      getRollsList(),
      getProductList(),
      getSupplierList(),
      getBatchList()
    ])

    rollList.value = rolls
    productsList.value = products
    supplierList.value = suppliers
    batchList.value = batches
  } catch (error: any) {
    console.error('Error loading data:', error)
    showToast('error', 'Error', 'Failed to load required data.')
  } finally {
    isLoadingData.value = false
  }
})
</script>

<template>
  <div class="flex justify-between items-center mb-10">
    <RouterLink to="/rolls" class="flex items-center gap-x-2 group hover:text-primary-default">
      <Icon
          icon="material-symbols:arrow-back-rounded"
          class="text-3xl group-hover:translate-x-2 transition-transform"
      />
      <span>Back</span>
    </RouterLink>

    <h1 class="font-semibold text-2xl">Send Rolls to Supplier</h1>

    <div class="flex flex-row justify-between items-center gap-x-4">
      <Button label="Send to Supplier" @click="submit" :loading="loading">
        <template #icon>
          <Icon icon="mdi:send" class="text-xl ml-3 order-1" />
        </template>
      </Button>
    </div>
  </div>

  <div v-if="isLoadingData" class="flex justify-center items-center mt-10">
    <ProgressSpinner strokeWidth="4" style="width: 50px; height: 50px" animationDuration=".5s" />
    <p class="ml-3 text-lg text-gray-600">Loading data...</p>
  </div>

  <div v-else class="bg-white rounded-xl pb-2 px-4 py-4">
    <form @submit.prevent="submit">
      <div class="flex justify-between flex-row w-full gap-16 my-8">
        <FormInputText
            label="Batch Number"
            inputName="batchNumber"
            id="batchNumber"
            placeholder="Enter batch number"
            class="w-1/2"
        />
        <FormDropdown
            label="Knitting Quality"
            inputName="knittingQualityId"
            id="knittingQualityId"
            :options="productsList.filter(product => product.fabricStatus === 1)"
            optionLabel="unfinishedReference"
            optionValue="id"
            class="w-1/2"
        />
      </div>

      <div class="flex justify-between flex-row w-full gap-16 my-8">
        <FormDropdown
            label="Finishing Quality"
            inputName="finishingQualityId"
            id="finishingQualityId"
            :options="productsList.filter(product => product.fabricStatus === 0)"
            optionLabel="finishedReference"
            optionValue="id"
            class="w-1/2"
        />
        <FormDropdown
            label="Supplier Name"
            inputName="finishingSupplierId"
            id="finishingSupplierId"
            :options="filteredSuppliers"
            optionLabel="name"
            optionValue="id"
            class="w-1/2"
        />
      </div>

      <div class="flex justify-between flex-row w-full gap-16 my-8">
        <FormDropdown
            label="Supplier Address"
            inputName="finishingSupplierAddressId"
            id="finishingSupplierAddressId"
            :options="formattedSupplierAddress"
            optionLabel="label"
            optionValue="id"
            class="w-1/2"
        />
        <FormInputText
            label="Purchase Order Number"
            inputName="purchaseOrderNumber"
            id="purchaseOrderNumber"
            class="w-1/2"
        />
      </div>

      <div class="flex justify-between flex-row w-full gap-16 my-8">
        <FormInputText
            label="Bay"
            inputName="bay"
            id="bay"
            placeholder="Enter bay location"
            class="w-1/2"
        />
        <div class="w-1/2"></div> <!-- Empty space for alignment -->
      </div>

      <div class="flex flex-col gap-4 my-8">
        <h3 class="font-semibold text-xl">Select Rolls to Send</h3>
        <p class="text-sm text-gray-600">
          Available rolls: {{ filteredRollList.length }} | Selected: {{ selectedRolls.length }}
        </p>

        <div class="flex flex-row gap-2 items-center mb-4">
          <Checkbox v-model="selectAll" :binary="true" />
          <label class="text-sm font-medium"> Select All Available Rolls</label>
        </div>

        <div v-if="filteredRollList.length === 0" class="text-center py-8 text-gray-500">
          <p>No rolls available for the selected quality.</p>
          <p class="text-sm">Please select a knitting quality first.</p>
        </div>

        <div v-else class="grid grid-cols-4 gap-4 max-h-96 overflow-y-auto border rounded-lg p-4">
          <div
              v-for="roll in filteredRollList"
              :key="roll.id"
              class="flex flex-col gap-2 p-3 border rounded-lg hover:bg-gray-50"
              :class="{ 'bg-blue-50 border-blue-300': isRollSelected(roll.id) }"
          >
            <div class="flex items-center gap-2">
              <Checkbox
                  :modelValue="isRollSelected(roll.id)"
                  @update:modelValue="handleRollSelection(roll.id)"
                  :binary="true"
              />
              <label class="font-medium">Roll {{ roll.rollNumber }}</label>
            </div>
            <div class="text-xs text-gray-500">
              <p>Weight: {{ roll.weight }}kg</p>
              <p>Batch Number: {{ getBatchNumber(roll?.batchId || null) }}</p>
              <p>Bay: {{ roll.bay || 'N/A' }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-between flex-row w-full gap-16 my-8">
        <FormTextArea
            label="Notes"
            inputName="notes"
            id="notes"
            placeholder="Enter any additional notes..."
            class="w-full"
        />
      </div>
    </form>
  </div>
</template>